/**
 * Formula Tokenizer
 *
 * Converts a formula string into a stream of tokens for the parser.
 */

import type { FormulaToken } from '@mesterplan/shared-types';
import { FORMULA_FUNCTIONS } from '@mesterplan/shared-types';

const FUNCTION_NAMES = new Set<string>(FORMULA_FUNCTIONS);

export class FormulaTokenizerError extends Error {
  constructor(
    message: string,
    public position: number
  ) {
    super(message);
    this.name = 'FormulaTokenizerError';
  }
}

export function tokenize(input: string): FormulaToken[] {
  const tokens: FormulaToken[] = [];
  let position = 0;

  while (position < input.length) {
    const char = input.charAt(position);

    // Skip whitespace
    if (/\s/.test(char)) {
      position++;
      continue;
    }

    // Numbers (including decimals)
    const nextChar = position + 1 < input.length ? input.charAt(position + 1) : '';
    if (/\d/.test(char) || (char === '.' && /\d/.test(nextChar))) {
      const start = position;
      let hasDecimal = char === '.';

      if (char !== '.') {
        position++;
      }

      while (position < input.length) {
        const c = input.charAt(position);
        if (/\d/.test(c)) {
          position++;
        } else if (c === '.' && !hasDecimal) {
          hasDecimal = true;
          position++;
        } else {
          break;
        }
      }

      tokens.push({
        type: 'NUMBER',
        value: input.slice(start, position),
        position: start,
        length: position - start,
      });
      continue;
    }

    // Identifiers (field names, including dot notation like projects.revenue) and functions
    if (/[a-zA-Z_]/.test(char)) {
      const start = position;
      position++;

      // Allow alphanumeric, underscore, and dots for prefixed field names
      while (position < input.length && /[a-zA-Z0-9_.]/.test(input.charAt(position))) {
        position++;
      }

      const value = input.slice(start, position);
      const upperValue = value.toUpperCase();

      // Check if it's a function name
      if (FUNCTION_NAMES.has(upperValue)) {
        tokens.push({
          type: 'FUNCTION',
          value: upperValue,
          position: start,
          length: position - start,
        });
      } else {
        tokens.push({
          type: 'IDENTIFIER',
          value,
          position: start,
          length: position - start,
        });
      }
      continue;
    }

    // Operators and delimiters
    switch (char) {
      case '(':
        tokens.push({ type: 'LPAREN', value: '(', position, length: 1 });
        position++;
        continue;
      case ')':
        tokens.push({ type: 'RPAREN', value: ')', position, length: 1 });
        position++;
        continue;
      case '+':
        tokens.push({ type: 'PLUS', value: '+', position, length: 1 });
        position++;
        continue;
      case '-':
        tokens.push({ type: 'MINUS', value: '-', position, length: 1 });
        position++;
        continue;
      case '*':
        tokens.push({ type: 'MULTIPLY', value: '*', position, length: 1 });
        position++;
        continue;
      case '/':
        tokens.push({ type: 'DIVIDE', value: '/', position, length: 1 });
        position++;
        continue;
      case ',':
        tokens.push({ type: 'COMMA', value: ',', position, length: 1 });
        position++;
        continue;
      default:
        throw new FormulaTokenizerError(`Ukendt tegn: "${char}"`, position);
    }
  }

  tokens.push({ type: 'EOF', value: '', position, length: 0 });
  return tokens;
}
